import { type FC, type FormEvent, type KeyboardEvent, useRef, useState } from "react";

import { ActionIcon, Textarea } from "@mantine/core";
import { ArrowUpIcon } from "@snapchat/snap-design-system-icons";
import { useIntl } from "react-intl";

import styles from "./AssistantChatInput.module.css";
import { messages } from "./messages.generated";

interface Props {
    disabled: boolean;
    onSend: (question: string) => void;
}

const SEND_ICON_SIZE = 16;

export const AssistantChatInput: FC<Props> = ({ disabled, onSend }) => {
    const intl = useIntl();
    const [value, setValue] = useState("");
    const compositionInProgress = useRef(false);

    const submit = (): void => {
        const trimmed = value.trim();
        if (trimmed.length === 0 || disabled) return;
        onSend(trimmed);
        setValue("");
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        submit();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
        if (compositionInProgress.current || event.nativeEvent.isComposing) return;
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submit();
        }
    };

    return (
        <form className={styles.composer} onSubmit={handleSubmit}>
            <Textarea
                classNames={{ root: styles.inputRoot, input: styles.input }}
                variant="unstyled"
                value={value}
                onChange={(event) => {
                    setValue(event.currentTarget.value);
                }}
                onKeyDown={handleKeyDown}
                onCompositionStart={() => {
                    compositionInProgress.current = true;
                }}
                onCompositionEnd={() => {
                    compositionInProgress.current = false;
                }}
                placeholder={intl.formatMessage(messages.inputPlaceholder)}
                autosize
                minRows={1}
                maxRows={5}
                disabled={disabled}
                aria-label={intl.formatMessage(messages.inputPlaceholder)}
            />
            <ActionIcon
                type="submit"
                className={styles.sendButton}
                radius="xl"
                disabled={disabled || value.trim().length === 0}
                aria-label={intl.formatMessage(messages.sendAriaLabel)}
            >
                <ArrowUpIcon size={SEND_ICON_SIZE} />
            </ActionIcon>
        </form>
    );
};
